import {
  productImageListResponseSchema,
  productImageSchema,
  type ProductImage,
  type ProductImageListResponse,
  type ProductImageSortInput,
  type ProductImageUpdateInput,
  type ProductImageVariantName,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiSend, apiUpload, type ApiError } from '@/lib/api-client';
import { productKeys } from './use-products';

export const productImageKeys = {
  list: (productId: string) => ['products', 'detail', productId, 'images'] as const,
};

export function productImageUrl(
  productId: string,
  imageId: string,
  variant: ProductImageVariantName | 'original',
): string {
  return `/api/v1/products/${encodeURIComponent(productId)}/images/${encodeURIComponent(imageId)}/${variant}`;
}

export function useProductImages(
  productId: string,
): UseQueryResult<ProductImageListResponse, ApiError> {
  return useQuery({
    queryKey: productImageKeys.list(productId),
    queryFn: () => apiFetch(`/api/v1/products/${productId}/images`, productImageListResponseSchema),
  });
}

interface UploadVariables {
  files: File[];
  title?: string | undefined;
  altText?: string | undefined;
  onProgress?: ((percentage: number) => void) | undefined;
  signal?: AbortSignal | undefined;
}

function imageFormData(input: UploadVariables): FormData {
  const form = new FormData();
  for (const file of input.files) form.append('files', file, file.name);
  if (input.title !== undefined) form.set('title', input.title);
  if (input.altText !== undefined) form.set('altText', input.altText);
  return form;
}

export function uploadProductImageFiles(
  productId: string,
  input: UploadVariables & { signal?: AbortSignal | undefined },
): Promise<ProductImageListResponse> {
  return apiUpload(`/api/v1/products/${productId}/images`, productImageListResponseSchema, {
    formData: imageFormData(input),
    onProgress: input.onProgress,
    signal: input.signal,
  });
}

function useInvalidateProductMedia(productId: string): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: productImageKeys.list(productId) });
    void queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
    void queryClient.invalidateQueries({ queryKey: productKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['product-catalog'] });
  };
}

export function useUploadProductImages(
  productId: string,
): UseMutationResult<ProductImageListResponse, ApiError, UploadVariables> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: (input) => uploadProductImageFiles(productId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateProductImage(
  productId: string,
): UseMutationResult<ProductImage, ApiError, { imageId: string; input: ProductImageUpdateInput }> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: ({ imageId, input }) =>
      apiFetch(`/api/v1/products/${productId}/images/${imageId}`, productImageSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

export function useSortProductImages(
  productId: string,
): UseMutationResult<ProductImageListResponse, ApiError, ProductImageSortInput> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: (input) =>
      apiFetch(`/api/v1/products/${productId}/images/order`, productImageListResponseSchema, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryProductImage(
  productId: string,
): UseMutationResult<ProductImageListResponse, ApiError, string> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: (imageId) =>
      apiFetch(`/api/v1/products/${productId}/images/primary`, productImageListResponseSchema, {
        method: 'PUT',
        body: { imageId },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteProductImage(
  productId: string,
): UseMutationResult<void, ApiError, string> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: (imageId) =>
      apiSend(`/api/v1/products/${productId}/images/${imageId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useReplaceProductImage(
  productId: string,
): UseMutationResult<
  ProductImage,
  ApiError,
  { imageId: string; file: File; onProgress?: ((percentage: number) => void) | undefined }
> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: ({ imageId, file, onProgress }) =>
      apiUpload(`/api/v1/products/${productId}/images/${imageId}/content`, productImageSchema, {
        method: 'PUT',
        formData: imageFormData({ files: [file] }),
        onProgress,
      }),
    onSuccess: invalidate,
  });
}

export function useRetryProductImage(
  productId: string,
): UseMutationResult<ProductImage, ApiError, string> {
  const invalidate = useInvalidateProductMedia(productId);
  return useMutation({
    mutationFn: (imageId) =>
      apiFetch(`/api/v1/products/${productId}/images/${imageId}/retry`, productImageSchema, {
        method: 'POST',
      }),
    onSuccess: invalidate,
  });
}
